import { getGlobal } from './apiClients/global';
import { constructRequest, sendChirpFetchRequest, sendHubHttpRequest } from './chirp-avro-fetch-base';
import { ChirpInternalError } from './chirp-avro';
import { shouldForce401 } from './localRequestUtils';
/**
 * No-op BffMemcached used when the BFF fallback runs client-side.
 * The browser has no memcached, so all reads miss, all writes are no-ops,
 * and read-through (`getOrLoad`) always executes the loader.
 */
const NOOP_BFF_MEMCACHED = {
  get: () => Promise.resolve(undefined),
  set: () => Promise.resolve(),
  delete: () => Promise.resolve(),
  getOrLoad: (_key, load) => load()
};
/**
 * BFF-specific dispatcher. `details`, `bffArgs`, and `client` are forwarded
 * into the send callbacks so the local fallback can be invoked directly on
 * gateway errors — no intermediate error class required.
 *
 * hub-http path: hub-http rejects on all non-2xx, so `.catch` is unavoidable;
 * fallback is tried on non-auth, non-zero errors; if absent (or on auth/network
 * errors), 401 resolves as `{ isAuthError: true }`, other non-zero errors with
 * a JSON body reject with `{ status, responseJSON }`, and errors without a JSON
 * body propagate as-is.
 *
 * fetch path: `sendChirpFetchRequest` returns the raw `Response`; fallback is
 * tried on non-auth non-ok responses; if absent, 401/403 throw auth errors and
 * other non-ok responses reject with `{ status, responseJSON }`.
 */
function dispatchBffRequest(details, args, bffArgs, client) {
  const prepared = constructRequest(Object.assign({}, args, {
    headers: {}
  }));
  const invokeFallback = () => details.fallback != null && client != null ? details.fallback({
    client: {
      call: client.call,
      capture: client.call
    },
    memcached: NOOP_BFF_MEMCACHED
  }, bffArgs) : null;
  if (prepared.hubHttpClient) {
    return sendHubHttpRequest(Object.assign({}, prepared, {
      hubHttpClient: prepared.hubHttpClient
    })).catch(errorResponse => {
      const isAuthError = errorResponse.status === 401 || errorResponse.status === 403;
      const isNetworkError = errorResponse.status === 0;
      if (!isAuthError && !isNetworkError) {
        const fallbackResult = invokeFallback();
        if (fallbackResult != null) return fallbackResult;
      }
      if (errorResponse.status === 401 && 'responseJSON' in errorResponse) {
        return Object.assign({
          isAuthError: true
        }, errorResponse.responseJSON);
      }
      if (errorResponse.status !== 0 && 'responseJSON' in errorResponse) {
        throw Object.assign(new Error('BFF gateway error'), {
          status: errorResponse.status,
          responseJSON: errorResponse.responseJSON
        });
      }
      throw errorResponse;
    });
  }
  return sendChirpFetchRequest(prepared).then(response => {
    if (!response.ok) {
      const isAuthError = response.status === 401 || response.status === 403;
      if (!isAuthError) {
        const fallbackResult = invokeFallback();
        if (fallbackResult != null) return fallbackResult;
      }
      if (response.status === 401 || shouldForce401()) {
        throw Object.assign(new ChirpInternalError('Unauthorized'), {
          isAuthError: true
        });
      }
      return response.json().then(responseJSON => Promise.reject(Object.assign(new Error('BFF gateway error'), {
        status: response.status,
        responseJSON
      })), () => response.text().catch(() => undefined).then(responseText => Promise.reject(Object.assign(new Error(`BFF gateway error${responseText ? `: ${responseText}` : ''}`), {
        status: response.status
      }))));
    }
    if (shouldForce401()) {
      throw Object.assign(new ChirpInternalError('Unauthorized'), {
        isAuthError: true
      });
    }
    return response.json();
  });
}

/**
 * Marker for client-types code generation.
 * BFF calls should use client-types generated bindings.
 */
export function defineBff(_metadata, f) {
  return f;
}
export function deriveAuthFromDomain(hostname) {
  if (/\.hubspot(qa)?\.com$/.test(hostname)) {
    return 'app';
  }
  return 'internal';
}
export function getBffGateway(auth) {
  return `/chirp-bff-${auth}/v1`;
}
export function makeBffCall({
  hubHttpClient,
  details,
  inputs,
  responseParser,
  calledFrom = 'bend',
  rpcFetcherFactory,
  fetcherOverride,
  alternateData,
  client
}) {
  var _details$target, _location$hostname, _location, _inputs$args, _inputs$args2;
  const auth = (_details$target = details.target) !== null && _details$target !== void 0 ? _details$target : deriveAuthFromDomain((_location$hostname = (_location = getGlobal().location) === null || _location === void 0 ? void 0 : _location.hostname) !== null && _location$hostname !== void 0 ? _location$hostname : '');
  const {
    timeout,
    portalId,
    userId,
    environment,
    hublet
  } = inputs !== null && inputs !== void 0 ? inputs : {};
  const request = {
    args: [(_inputs$args = inputs === null || inputs === void 0 ? void 0 : inputs.args) !== null && _inputs$args !== void 0 ? _inputs$args : {}]
  };
  const {
    bffHash,
    name
  } = details;
  const syncStackError = new Error(`BFF call failed for ${bffHash}`);
  const clientTimeout = typeof timeout === 'number' ? timeout : 5000;
  const gatewayUrl = `${getBffGateway(auth)}/execute/hash/${bffHash}/${name}`;
  const data = alternateData !== null && alternateData !== void 0 ? alternateData : dispatchBffRequest(details, {
    gatewayUrl,
    request,
    auth,
    clientTimeout,
    environment,
    hublet,
    portalId,
    userId,
    calledFrom,
    hubHttpClient,
    rpcFetcherFactory,
    fetcherOverride
  }, (_inputs$args2 = inputs === null || inputs === void 0 ? void 0 : inputs.args) !== null && _inputs$args2 !== void 0 ? _inputs$args2 : {}, client);
  return data.then(response => {
    if (response != null && typeof response === 'object' && response.isAuthError === true) {
      throw Object.assign(new Error('Unauthorized'), {
        isAuthError: true
      });
    }
    return responseParser ? responseParser(response) : response;
  }).catch(error => {
    syncStackError.cause = error;
    throw syncStackError;
  });
}
export function createBffClient({
  hubHttpClient,
  fetcherOverride,
  rpcFetcherFactory,
  calledFrom,
  client
}) {
  return {
    call: (details, inputs, alternateData) => makeBffCall({
      hubHttpClient,
      details,
      inputs,
      calledFrom,
      rpcFetcherFactory,
      fetcherOverride,
      alternateData,
      client
    })
  };
}